<template>
  <ToolPreviewLayout>
    <div
      class="flex h-[90vh] w-full flex-col overflow-hidden rounded-2xl border border-gray-300 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.1)]"
    >
      <SettingsBar
        :groups="settingsGroups"
        @select="showWarningModal"
      />

      <postcard-designer
        :key="`${selectedSize}-${selectedSide}-${sidebarCollapsed}`"
        :api-key
        :config="JSON.stringify(designerToolConfig)"
        class="block h-full min-h-0 w-full flex-1"
        @html-button-click="onDesignerSave"
      />

      <div class="flex items-center justify-between gap-x-8 gap-y-2 border-t border-gray-300 px-6 py-3 text-sm text-gray-600">
        <span class="flex items-center gap-1.5 font-medium text-green-600">
          <CheckIcon class="size-4" />
          Passes Lob and PostGrid preflight
        </span>
        <span>Click any text on the postcard to edit it. The HTML updates as you type.</span>
      </div>
    </div>
  </ToolPreviewLayout>

  <ConfirmDialog
    :show="isShowWarningModal"
    max-width="md"
    title="Change configuration"
    message="Are you sure you want to change the configuration? Unsaved changes will be lost."
    @close="closeDialog"
    @confirm="applyChanges"
  />

  <Modal
    :show="isShowRegisterModal"
    max-width="md"
    @close="closeRegisterModal"
  >
    <div class="p-4 text-left sm:p-6 lg:px-10 lg:py-8">
      <RegisterForm @success="closeRegisterModal" />
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { CheckIcon } from "lucide-vue-next";
import { buildDesignerToolConfig } from "@/utils/designerToolConfig";
import ConfirmDialog from "@/Components/ConfirmDialog.vue";
import Modal from "@/Components/Modal.vue";
import RegisterForm from "@/Components/Auth/RegisterForm.vue";
import SettingsBar from "@/Components/SettingsBar.vue";
import ToolPreviewLayout from "@/Layouts/ToolPreviewLayout.vue";
import icon4x6 from "@/../images/icons/4x6.svg";
import icon6x9 from "@/../images/icons/6x9.svg";
import icon6x11 from "@/../images/icons/6x11.svg";
import iconLetter from "@/../images/icons/letter.svg";
import iconFront from "@/../images/icons/front.svg";
import iconBack from "@/../images/icons/back.svg";

const apiKey = import.meta.env.VITE_SANDBOX_API_KEY ?? "testing_key";

const sizes = [
  { id: "4x6", label: '4" × 6"', icon: icon4x6 },
  { id: "6x9", label: '6" × 9"', icon: icon6x9 },
  { id: "6x11", label: '6" × 11"', icon: icon6x11 },
  { id: "11x8.5", label: '11" × 8.5"', icon: iconLetter },
];

const sides = [
  { id: "front", label: "Front", icon: iconFront },
  { id: "back", label: "Back", icon: iconBack },
];

const selectedSize = ref("4x6");
const selectedSide = ref("front");
const sidebarCollapsed = ref(true);
const isShowWarningModal = ref(false);
const isShowRegisterModal = ref(false);
const pendingValue = ref<string | null>(null);

const settingsGroups = computed(() => [
  { key: "size", label: "Size", options: sizes, selected: selectedSize.value },
  { key: "side", label: "Side", options: sides, selected: selectedSide.value },
]);

const designerToolConfig = computed(() =>
  buildDesignerToolConfig({
    size: selectedSize.value,
    side: selectedSide.value,
    type: selectedSize.value === "11x8.5" ? "letter" : "postcard",
    isWrappedInModal: true,
    is_preview: true,
    isTopRightExitButtonVisible: false,
    isBottomLeftExitButtonVisible: false,
  }),
);

function showWarningModal(_groupKey: string, newValue: string) {
  isShowWarningModal.value = true;
  pendingValue.value = newValue;
}

function closeDialog() {
  isShowWarningModal.value = false;
  pendingValue.value = null;
}

function onDesignerSave() {
  isShowRegisterModal.value = true;
}

function closeRegisterModal() {
  isShowRegisterModal.value = false;
}

function applyChanges() {
  if (!pendingValue.value) return;

  sessionStorage.clear();

  if (sizes.some((size) => size.id === pendingValue.value)) {
    selectedSize.value = pendingValue.value;
  } else if (sides.some((side) => side.id === pendingValue.value)) {
    selectedSide.value = pendingValue.value;
  }

  closeDialog();
}
</script>
